import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, List as ListIcon, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MiniTrend } from "@/components/recovery/mini-trend";
import { ApiError, createList, getLists, type ListSummary } from "@/lib/api";
import { relativeDay } from "@/lib/format";

function meta(l: ListSummary): string {
  const items = `${l.item_count} item${l.item_count === 1 ? "" : "s"}`;
  if (l.logged_today) return `${items} · logged today`;
  if (l.last_logged_at) return `${items} · last logged ${relativeDay(l.last_logged_at).toLowerCase()}`;
  return `${items} · not logged yet`;
}

export default function Lists() {
  const navigate = useNavigate();
  const [lists, setLists] = useState<ListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setLists(await getLists());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't load your lists.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await createList(name.trim());
      setName("");
      setAdding(false);
      await load();
    } catch {
      setError("Couldn't create the list.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">Custom Lists</h1>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          <p className="py-16 text-center text-sm text-muted-foreground">{error}</p>
        ) : (
          <>
            {lists.length === 0 && !adding && (
              <Card>
                <CardContent className="px-4 py-6 text-center">
                  <p className="text-sm font-medium text-foreground">No lists yet</p>
                  <p className="mt-1 text-[13px] text-muted-foreground">
                    Track anything that matters — triggers, gratitude, sleep.
                  </p>
                </CardContent>
              </Card>
            )}

            {lists.map((l) => (
              <Card interactive key={l.id} onClick={() => navigate(`/lists/${l.id}`)}>
                <CardContent className="flex items-center gap-3 px-4 py-3.5">
                  <IconChip icon={ListIcon} tint="primary" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-foreground">{l.name}</p>
                    <p className="mb-1.5 text-xs text-muted-foreground">{meta(l)}</p>
                    <MiniTrend data={l.trend.slice(7)} className="max-w-[160px]" />
                  </div>
                  <ChevronRight className="h-[18px] w-[18px] text-muted-foreground" strokeWidth={2} />
                </CardContent>
              </Card>
            ))}

            {adding ? (
              <Card>
                <CardContent className="flex flex-col gap-2 p-3">
                  <Input
                    autoFocus
                    placeholder="Name your list (e.g. Triggers)"
                    value={name}
                    maxLength={60}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && save()}
                  />
                  <div className="flex gap-2">
                    <Button disabled={!name.trim() || saving} onClick={save}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Create"}
                    </Button>
                    <Button variant="ghost" onClick={() => { setAdding(false); setName(""); }}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button variant="outline" fullWidth onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" strokeWidth={2} />
                New List
              </Button>
            )}
            <p className="text-center text-xs text-muted-foreground">
              Track anything that matters — triggers, gratitude, sleep.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
